import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyProfile, updateMyTheme } from "@/lib/user.functions";
import { supabase } from "@/integrations/supabase/client";

type Theme = "light" | "dark" | "system";
type Ctx = { theme: Theme; resolved: "light" | "dark"; setTheme: (t: Theme) => void };

const ThemeCtx = createContext<Ctx>({ theme: "system", resolved: "light", setTheme: () => {} });

const STORAGE_KEY = "btr-theme";

function applyTheme(theme: Theme): "light" | "dark" {
  const resolved: "light" | "dark" =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  document.documentElement.classList.toggle("dark", resolved === "dark");
  return resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("system");
  const [resolved, setResolved] = useState<"light" | "dark">("light");
  const updateThemeFn = useServerFn(updateMyTheme);
  const profileFn = useServerFn(getMyProfile);

  // Hidrata do localStorage no mount para evitar flash
  useEffect(() => {
    const local = (typeof window !== "undefined" &&
      localStorage.getItem(STORAGE_KEY)) as Theme | null;
    const initial = local ?? "system";
    setThemeState(initial);
    setResolved(applyTheme(initial));
  }, []);

  // Sincroniza com preferência persistida no perfil (se logado)
  const { data: profile } = useQuery({
    queryKey: ["my-profile-theme"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return null;
      return profileFn();
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    const remote = profile?.theme_preference as Theme | undefined;
    if (remote && remote !== theme) {
      setThemeState(remote);
      setResolved(applyTheme(remote));
      localStorage.setItem(STORAGE_KEY, remote);
    }
  }, [profile]); // eslint-disable-line

  // Reage a mudanças do sistema quando em "system"
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setResolved(applyTheme("system"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    setResolved(applyTheme(t));
    localStorage.setItem(STORAGE_KEY, t);
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) updateThemeFn({ data: { theme: t } }).catch(() => {});
    });
  };

  return <ThemeCtx.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
